import type { InterviewData } from '../types';
import { getCountedProgressSteps } from './conversation-progress-summary';

/** Summarize persisted active-engine evidence without using the current campaign schema. */
export function getResponseProgressSummary(response: Pick<InterviewData, 'conversation_progress'>) {
  const progress = response.conversation_progress;
  if (progress?.mode !== 'active' || !Array.isArray(progress.steps)) return null;
  const steps = getCountedProgressSteps(progress.steps);
  if (steps.length === 0) return null;
  return {
    answered: steps.filter(step => step.state === 'completed').length,
    skipped: steps.filter(step => step.state === 'skipped' && !step.required).length,
    total: steps.length,
  };
}

/** Recorded active time only; asynchronous text gaps are not a duration estimate. */
export function getResponseActiveDurationMs(response: Pick<InterviewData, 'duration_segments'>): number | null {
  const segments = response.duration_segments?.filter(segment => Number.isFinite(segment.start_ms)
    && Number.isFinite(segment.end_ms) && segment.end_ms >= segment.start_ms);
  if (!segments?.length) return null;
  return segments.reduce((total, segment) => total + segment.end_ms - segment.start_ms, 0);
}

/** Display the same recorded duration used for numeric table sorting. */
export function getResponseActiveDuration(response: Pick<InterviewData, 'duration_segments'>): string | null {
  const milliseconds = getResponseActiveDurationMs(response);
  if (milliseconds === null) return null;
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
}
